import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LoginPage } from "@/pages/LoginPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { ProjectsPage } from "@/pages/board/ProjectsPage";
import { BoardPage } from "@/pages/board/BoardPage";
import { DeploymentsPage } from "@/pages/deployments/DeploymentsPage";
import { DeploymentGroupPage } from "@/pages/deployments/DeploymentGroupPage";
import { EnvMatrixPage } from "@/pages/deployments/EnvMatrixPage";
import { ChatPage } from "@/pages/ChatPage";
import { DocsPage } from "@/pages/DocsPage";
import { TeamPage } from "@/pages/TeamPage";
import { CustomersPage } from "@/pages/crm/CustomersPage";
import { CompetitionPage } from "@/pages/CompetitionPage";
import { FinancePage } from "@/pages/FinancePage";
import { PipelinePage } from "@/pages/crm/PipelinePage";
import { InvestorsPage } from "@/pages/crm/InvestorsPage";
import { AuditPage } from "@/pages/AuditPage";
import { ComplianceOverviewPage } from "@/pages/compliance/ComplianceOverviewPage";
import { ComplianceControlsPage } from "@/pages/compliance/ComplianceControlsPage";
import { ComplianceFrameworksPage } from "@/pages/compliance/ComplianceFrameworksPage";
import { CompliancePoliciesPage } from "@/pages/compliance/CompliancePoliciesPage";
import { ComplianceOperatingPage } from "@/pages/compliance/ComplianceOperatingPage";
import { ComplianceRisksPage } from "@/pages/compliance/ComplianceRisksPage";
import { ComplianceSoaPage } from "@/pages/compliance/ComplianceSoaPage";
import { CompliancePeoplePage } from "@/pages/compliance/CompliancePeoplePage";
import { ComplianceVendorsPage } from "@/pages/compliance/ComplianceVendorsPage";
import { ComplianceAuditPage } from "@/pages/compliance/ComplianceAuditPage";
import { ComplianceConnectorsPage } from "@/pages/compliance/ComplianceConnectorsPage";
import { AuditorViewPage } from "@/pages/AuditorViewPage";

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* The auditor's window: no session, outside the app shell entirely. */}
          <Route path="/auditor/:token" element={<AuditorViewPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/chat" replace />} />
            <Route path="board" element={<ProjectsPage />} />
            <Route path="board/:projectId" element={<BoardPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="deployments" element={<DeploymentsPage />} />
            {/* A deployment, optionally with one of its services selected. */}
            <Route path="deployments/:groupKey/:serviceId?" element={<DeploymentGroupPage />} />
            <Route path="env" element={<EnvMatrixPage />} />
            {/* Every doc-hub view is linkable: a folder, a quick-access list,
                and ?file=<id> to open one file wherever it lives. */}
            <Route path="docs" element={<DocsPage />} />
            <Route path="docs/folder/:folderId" element={<DocsPage />} />
            <Route path="docs/:section" element={<DocsPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="pipeline" element={<PipelinePage />} />
            <Route path="investors" element={<InvestorsPage />} />
            <Route path="competition" element={<CompetitionPage />} />
            {/* Finance's tabs are routes so the section nav can list them. */}
            <Route path="finance" element={<FinancePage />} />
            <Route path="finance/:tab" element={<FinancePage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="compliance" element={<ComplianceOverviewPage />} />
            <Route path="compliance/controls" element={<ComplianceControlsPage />} />
            <Route path="compliance/frameworks" element={<ComplianceFrameworksPage />} />
            <Route path="compliance/policies" element={<CompliancePoliciesPage />} />
            <Route path="compliance/operating" element={<ComplianceOperatingPage />} />
            <Route path="compliance/risks" element={<ComplianceRisksPage />} />
            <Route path="compliance/soa" element={<ComplianceSoaPage />} />
            <Route path="compliance/people" element={<CompliancePeoplePage />} />
            <Route path="compliance/vendors" element={<ComplianceVendorsPage />} />
            <Route path="compliance/audit" element={<ComplianceAuditPage />} />
            <Route path="compliance/connectors" element={<ComplianceConnectorsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
